import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { lead_type: "contact", ...form });
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error("Unable to send. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10">
      <div className="border-b border-white/10 pb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Contact</div>
        <h1 className="mt-4 font-display text-5xl font-black uppercase leading-none text-white sm:text-6xl">
          Let's move some iron.
        </h1>
        <p className="mt-4 max-w-xl text-base text-white/60">
          Tell us what you need. We'll match you with the right machine, price, and delivery window — typically within one business day. Headquartered in Texas; we ship to any part of the United States.
        </p>
      </div>

      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="border border-[#27272A] bg-[#141415]">
            <div className="border-b border-[#27272A] p-6">
              <h2 className="font-display text-2xl font-bold uppercase">Houston Yard</h2>
              <p className="mt-2 font-mono text-xs uppercase tracking-widest text-white/50">Main Location</p>
            </div>
            <div className="divide-y divide-[#27272A]">
              <InfoRow icon={MapPin} label="Address" value="4200 FM 1960 East\nHouston, TX 77032" />
              <InfoRow icon={Phone} label="Phone" value="(800) 555-0123" href="tel:+18005550123" />
              <InfoRow icon={Mail} label="Email" value="support@texasironheavi.com" href="mailto:support@texasironheavi.com" />
              <InfoRow icon={Clock} label="Hours" value="Mon–Fri 7am–6pm\nSat 8am–2pm\nSun Closed" />
            </div>
          </div>

          <div className="mt-6 border border-[#27272A] bg-[#141415] p-6">
            <h3 className="font-display text-lg font-bold uppercase">Additional Yards</h3>
            <ul className="mt-4 space-y-3 text-sm text-white/70">
              <li className="flex justify-between border-b border-[#27272A] pb-2">
                <span>Dallas</span><span className="font-mono text-xs text-[#FFB800]">(214) 555-0199</span>
              </li>
              <li className="flex justify-between border-b border-[#27272A] pb-2">
                <span>Austin</span><span className="font-mono text-xs text-[#FFB800]">(512) 555-0140</span>
              </li>
              <li className="flex justify-between border-b border-[#27272A] pb-2">
                <span>San Antonio</span><span className="font-mono text-xs text-[#FFB800]">(210) 555-0177</span>
              </li>
              <li className="flex justify-between">
                <span>Midland</span><span className="font-mono text-xs text-[#FFB800]">(432) 555-0112</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-[#27272A] bg-[#141415] p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
              <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
              <Field label="Phone *" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
              <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
            </div>
            <div className="mt-4">
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Message *</label>
              <textarea
                data-testid="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="What are you looking for? Timeline? Project details?"
                className="w-full border border-[#27272A] bg-[#0a0a0b] p-4 font-mono text-sm text-white placeholder:text-white/30 focus:border-[#FFB800] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-contact-btn"
              className="mt-6 w-full bg-[#FFB800] px-6 py-4 font-display text-sm font-bold uppercase tracking-widest text-black hover:bg-[#E5A600] disabled:opacity-50 sm:w-auto"
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#27272A] bg-[#0a0a0b] p-3 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
    />
  </div>
);

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = (
    <div className="flex gap-4 p-5 transition-colors hover:bg-[#0a0a0b]">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FFB800]" />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</div>
        <div className="mt-1 whitespace-pre-line text-sm text-white">{value.replace(/\\n/g, "\n")}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
};
